import i18next from 'i18next';
import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    createNewArticleImage,
    createNewArticleProductGroup,
    deleteArticleArticle,
    deleteArticleArticleAssignment,
    deleteArticleImage,
    deleteArticleProductGroup,
    getArticleArticle,
    getArticleArticleAssignment,
    getArticleCustomField,
    getArticleImage,
    getArticleProductGroups,
    getArticleProperty,
    updateArticleImage,
    updateArticleProductGroup,
    updateExistingArticleArticle,
} from 'src/api/article-management';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';
import {
    CREATE_NEW_ARTICLE_ARTICLE_PRODUCT_GROUP,
    DEACTIVATE_ARTICLE_ARTICLE,
    DELETE_ARTICLE_ARTICLE_ASSIGNMENT,
    DELETE_ARTICLE_ARTICLE_IMAGE,
    DELETE_ARTICLE_ARTICLE_PRODUCT_GROUP,
    fetchArticleArticleProductGroup,
    FETCH_ARTICLE_ARTICLE,
    FETCH_ARTICLE_ARTICLE_ASSIGNMENT,
    FETCH_ARTICLE_ARTICLE_CUSTOM_FIELD,
    FETCH_ARTICLE_ARTICLE_IMAGE,
    FETCH_ARTICLE_ARTICLE_PRODUCT_GROUP,
    FETCH_ARTICLE_ARTICLE_PROPERTY,
    requestArticleArticle,
    requestArticleArticleAssignmentFailure,
    requestArticleArticleAssignmentSuccess,
    requestArticleArticleCustomField,
    requestArticleArticleCustomFieldFailure,
    requestArticleArticleCustomFieldSuccess,
    requestArticleArticleFailure,
    requestArticleArticleImage,
    requestArticleArticleImageFailure,
    requestArticleArticleImageSuccess,
    requestArticleArticleProductGroup,
    requestArticleArticleProductGroupFailure,
    requestArticleArticleProductGroupSuccess,
    requestArticleArticleProperty,
    requestArticleArticlePropertyFailure,
    requestArticleArticlePropertySuccess,
    requestArticleArticleSuccess,
    requestCreateNewArticleArticleProductGroup,
    requestCreateNewArticleArticleProductGroupFailure,
    requestCreateNewArticleArticleProductGroupSuccess,
    requestDeactivateArticleArticle,
    requestDeactivateArticleArticleFailure,
    requestDeactivateArticleArticleSuccess,
    requestDeleteArticleArticleAssignment,
    requestDeleteArticleArticleAssignmentFailure,
    requestDeleteArticleArticleAssignmentSuccess,
    requestDeleteArticleArticleImage,
    requestDeleteArticleArticleImageFailure,
    requestDeleteArticleArticleImageSuccess,
    requestDeleteArticleArticleProductGroup,
    requestDeleteArticleArticleProductGroupFailure,
    requestDeleteArticleArticleProductGroupSuccess,
    requestUpdateArticleArticle,
    requestUpdateArticleArticleFailure,
    requestUpdateArticleArticleImage,
    requestUpdateArticleArticleImageFailure,
    requestUpdateArticleArticleImageSuccess,
    requestUpdateArticleArticleProductGroup,
    requestUpdateArticleArticleProductGroupFailure,
    requestUpdateArticleArticleProductGroupSuccess,
    requestUpdateArticleArticleSuccess,
    requestUploadArticleArticleImage,
    requestUploadArticleArticleImageFailure,
    requestUploadArticleArticleImageSuccess,
    UPDATE_ARTICLE_ARTICLE,
    UPDATE_ARTICLE_ARTICLE_IMAGE,
    UPDATE_ARTICLE_ARTICLE_PRODUCT_GROUP,
    UPLOAD_ARTICLE_ARTICLE_IMAGE,
} from './actions';

function* fetchArticleArticleSaga(action: AnyAction): SagaIterator {
    yield put(requestArticleArticle());

    const { error, response } = yield call(
        getArticleArticle,
        action.pageable,
        action.articleFilter,
        action.excludeSetProducts
    );

    if (error) {
        yield put(requestArticleArticleFailure(error));
    } else {
        yield put(requestArticleArticleSuccess(response));
    }
}

function* updateArticleArticleSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateArticleArticle());

    const { error, response } = yield call(updateExistingArticleArticle, action.requestArticle);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.updateArticle.error') + '\n' + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestUpdateArticleArticleFailure(error));
    } else {
        yield put(requestUpdateArticleArticleSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.updateArticle.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* deactivateArticleArticleSaga(action: AnyAction): SagaIterator {
    yield put(requestDeactivateArticleArticle());

    const { error, response } = yield call(deleteArticleArticle, action.articleId);

    if (error) {
        yield put(requestDeactivateArticleArticleFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.deactivateArticle.error') + '\n' + error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestDeactivateArticleArticleSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.deactivateArticle.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateFetchArticleArticleWatcher() {
    yield takeLatest(FETCH_ARTICLE_ARTICLE, fetchArticleArticleSaga);
}

export function* generateUpdateArticleArticleWatcher() {
    yield takeLatest(UPDATE_ARTICLE_ARTICLE, updateArticleArticleSaga);
}

export function* generateDeactivateArticleArticleWatcher() {
    yield takeLatest(DEACTIVATE_ARTICLE_ARTICLE, deactivateArticleArticleSaga);
}

/* Article - Article Assignment */
function* fetchArticleArticleAssignmentSaga(action: AnyAction): SagaIterator {
    yield put(requestArticleArticle());

    const { error, response } = yield call(getArticleArticleAssignment, action.articleId);

    if (error) {
        yield put(requestArticleArticleAssignmentFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.setArticleForm.fetchArticleAssignmentError') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestArticleArticleAssignmentSuccess(response));
    }
}

export function* generateFetchArticleArticleAssignmentWatcher() {
    yield takeLatest(FETCH_ARTICLE_ARTICLE_ASSIGNMENT, fetchArticleArticleAssignmentSaga);
}

function* deleteArticleArticleAssignmentSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteArticleArticleAssignment());

    const { error, response } = yield call(deleteArticleArticleAssignment, action.articleAssignmentId);
    if (error) {
        yield put(requestDeleteArticleArticleAssignmentFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.setArticleForm.deleteArticleAssignmentError') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestDeleteArticleArticleAssignmentSuccess(response));
    }
}

export function* generateDeleteArticleArticleAssignmentWatcher() {
    yield takeLatest(DELETE_ARTICLE_ARTICLE_ASSIGNMENT, deleteArticleArticleAssignmentSaga);
}

/* Article Property */
function* fetchArticleArticlePropertySaga(action: AnyAction): SagaIterator {
    yield put(requestArticleArticleProperty());

    const { error, response } = yield call(getArticleProperty, action.articleId);

    if (error) {
        yield put(requestArticleArticlePropertyFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.articleProperty.fetchArticleProperty.error') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestArticleArticlePropertySuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.articleProperty.fetchArticleProperty.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateFetchArticlePropertyWatcher() {
    yield takeLatest(FETCH_ARTICLE_ARTICLE_PROPERTY, fetchArticleArticlePropertySaga);
}

/* Article Custom Field */
function* fetchArticleArticleCustomFieldSaga(action: AnyAction): SagaIterator {
    yield put(requestArticleArticleCustomField());

    const { error, response } = yield call(getArticleCustomField, action.articleId);

    if (error) {
        yield put(requestArticleArticleCustomFieldFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.customField.fetchCustomField.error') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestArticleArticleCustomFieldSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.customField.fetchCustomField.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateFetchArticleCustomFieldWatcher() {
    yield takeLatest(FETCH_ARTICLE_ARTICLE_CUSTOM_FIELD, fetchArticleArticleCustomFieldSaga);
}

/* Article Image */
function* fetchArticleArticleImageSaga(action: AnyAction): SagaIterator {
    yield put(requestArticleArticleImage());

    const { error, response } = yield call(getArticleImage, action.articleId);

    if (error) {
        yield put(requestArticleArticleImageFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.imageArticleForm.fetchArticleImageError') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestArticleArticleImageSuccess(response));
    }
}

function* uploadArticleArticleImageSaga(action: AnyAction): SagaIterator {
    yield put(requestUploadArticleArticleImage());

    const { error, response } = yield call(
        createNewArticleImage,
        action.articleImageInsertRequestDto,
        action.uploadFile
    );

    if (error) {
        yield put(requestUploadArticleArticleImageFailure(error));
    } else {
        yield put(requestUploadArticleArticleImageSuccess(response));
    }
}

function* updateArticleArticleImageSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateArticleArticleImage());

    const { error, response } = yield call(updateArticleImage, action.articleImageUpdateRequestDto);

    if (error) {
        yield put(requestUpdateArticleArticleImageFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.imageArticleForm.updateArticleImage.error') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestUpdateArticleArticleImageSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.imageArticleForm.updateArticleImage.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}
function* deleteArticleArticleImageSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteArticleArticleImage());

    const { error, response } = yield call(deleteArticleImage, action.articleImageId);

    if (error) {
        yield put(requestDeleteArticleArticleImageFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.imageArticleForm.deleteArticleImage.error') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestDeleteArticleArticleImageSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.imageArticleForm.deleteArticleImage.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateFetchArticleImageWatcher() {
    yield takeLatest(FETCH_ARTICLE_ARTICLE_IMAGE, fetchArticleArticleImageSaga);
}

export function* generateUploadArticleImageWatcher() {
    yield takeLatest(UPLOAD_ARTICLE_ARTICLE_IMAGE, uploadArticleArticleImageSaga);
}

export function* generateUpdateArticleImageWatcher() {
    yield takeLatest(UPDATE_ARTICLE_ARTICLE_IMAGE, updateArticleArticleImageSaga);
}

export function* generateDeleteArticleImageWatcher() {
    yield takeLatest(DELETE_ARTICLE_ARTICLE_IMAGE, deleteArticleArticleImageSaga);
}

/* Article Product Group */
function* fetchArticleArticleProductGroupSaga(action: AnyAction): SagaIterator {
    yield put(requestArticleArticleProductGroup());

    const { error, response } = yield call(getArticleProductGroups, action.articleId, action.productGroupId);

    if (error) {
        yield put(requestArticleArticleProductGroupFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.articleProductGroup.fetchArticleProductGroups.error') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestArticleArticleProductGroupSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t(
                    'articleManagement:pages.article.articleProductGroup.fetchArticleProductGroups.success'
                ),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* createNewArticleArticleProductGroupSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateNewArticleArticleProductGroup());

    const { error, response } = yield call(createNewArticleProductGroup, action.articleProductGroup);

    if (error) {
        yield put(requestCreateNewArticleArticleProductGroupFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.articleProductGroup.createArticleProductGroups.error') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestCreateNewArticleArticleProductGroupSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t(
                    'articleManagement:pages.article.articleProductGroup.createArticleProductGroups.success'
                ),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(fetchArticleArticleProductGroup(action.articleProductGroup.articleID));
    }
}

function* updateArticleArticleProductGroupSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateArticleArticleProductGroup());

    const { error, response } = yield call(updateArticleProductGroup, action.articleProductGroup);

    if (error) {
        yield put(requestUpdateArticleArticleProductGroupFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.articleProductGroup.updateArticleProductGroups.error') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestUpdateArticleArticleProductGroupSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t(
                    'articleManagement:pages.article.articleProductGroup.updateArticleProductGroups.success'
                ),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}
function* deleteArticleArticleProductGroupSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteArticleArticleProductGroup());

    const { error, response } = yield call(deleteArticleProductGroup, action.articleProductGroup.id);

    if (error) {
        yield put(requestDeleteArticleArticleProductGroupFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.articleProductGroup.deleteArticleProductGroups.error') +
                    '\n' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestDeleteArticleArticleProductGroupSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t(
                    'articleManagement:pages.article.articleProductGroup.deleteArticleProductGroups.success'
                ),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(fetchArticleArticleProductGroup(action.articleProductGroup.articleID));
    }
}

export function* generateFetchArticleArticleProductGroupWatcher() {
    yield takeLatest(FETCH_ARTICLE_ARTICLE_PRODUCT_GROUP, fetchArticleArticleProductGroupSaga);
}

export function* generateCreateArticleProductGroupWatcher() {
    yield takeLatest(CREATE_NEW_ARTICLE_ARTICLE_PRODUCT_GROUP, createNewArticleArticleProductGroupSaga);
}

export function* generateUpdateArticleProductGroupWatcher() {
    yield takeLatest(UPDATE_ARTICLE_ARTICLE_PRODUCT_GROUP, updateArticleArticleProductGroupSaga);
}

export function* generateDeleteArticleProductGroupWatcher() {
    yield takeLatest(DELETE_ARTICLE_ARTICLE_PRODUCT_GROUP, deleteArticleArticleProductGroupSaga);
}
